/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { AppState } from '@app/core/types';
import { productEditionNameAndMajorVersion } from '@app/domain/cluster';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ProductEdition } from '@shared/types/cluster';
import { DashboardTemplateId } from '@shared/types/monitoring';
import { memoize } from 'lodash-es';
import { currentClusterInfo, currentDashboard, selectUrl } from '.';
import { isCluster2xSelector, isCluster3xSelector } from './cluster.selectors';

const DEFAULT_DASHBOARD_ID_2X = '3c0b740c-3788-4133-baa7-b793383003ff';
export const DEFAULT_DASHBOARD_ID_3X = '15cb3478-4eeb-4146-9a78-d504f993e0fa';

export const dashboardTemplatesFeatureSelector =
  createFeatureSelector<AppState['dashboardTemplates']>('dashboardTemplates');

export const dashboardTemplatesSelector = createSelector(
  dashboardTemplatesFeatureSelector,
  isCluster2xSelector,
  (templates, is2x) => {
    return templates.filter((template) =>
      is2x
        ? !template.workspace.igniteBaseVersion || template.workspace.igniteBaseVersion === '2x'
        : template.workspace.igniteBaseVersion === '3x',
    );
  },
);

export const defaultDashboardTemplateSelector = createSelector(
  dashboardTemplatesSelector,
  isCluster2xSelector,
  (templates, is2x) =>
    templates.find(({ id }) => (is2x ? id === DEFAULT_DASHBOARD_ID_2X : id === DEFAULT_DASHBOARD_ID_3X)),
);
export const dashboardTemplateByIdSelector = memoize((id: DashboardTemplateId) =>
  createSelector(dashboardTemplatesSelector, (templates = []) => templates.find((t) => t.id === id)),
);
export const currentDashboardBaseTemplateHelp = createSelector(
  dashboardTemplatesSelector,
  currentDashboard,
  (templates, dashboard) => templates.find((template) => template.id === dashboard?.metadata.baseTemplateId)?.help,
);

const lastOpenedDashboardsSelector = createFeatureSelector<AppState['lastOpenedDashboard']>('lastOpenedDashboard');
export const lastOpenedDashboardForGroupSelector = (group: string) =>
  createSelector(lastOpenedDashboardsSelector, (groups) => groups[group]);

export const isOnMyClusterSelector = createSelector(
  selectUrl,
  // CC-9001 uncovered that selectUrl can return null, despite the type definition saying otherwise.
  (url: string | null): boolean => !!url?.match(/^\/clusters\/[a-f0-9\-]+\/monitoring-dashboard\/my-cluster/i),
);

export const currentDashboardProductNameAndMajorVersionSelector = createSelector(
  currentDashboard,
  currentClusterInfo,
  (dashboard, cluster) => {
    if (!dashboard || !cluster) return;
    const placeholderDashboardProductEdition =
      !dashboard.igniteBaseVersion || dashboard.igniteBaseVersion === '2x' ? ProductEdition.AI : ProductEdition.GG9CE;
    return productEditionNameAndMajorVersion(placeholderDashboardProductEdition);
  },
);

export const currentDashboardVersionMismatchedSelector = createSelector(
  currentDashboard,
  isCluster2xSelector,
  isCluster3xSelector,
  (dashboard, is2x, is3x): boolean => {
    if (!dashboard) return false;
    if (is2x && dashboard?.igniteBaseVersion === '3x') return true;
    if (is3x && (dashboard?.igniteBaseVersion === '2x' || !dashboard?.igniteBaseVersion)) return true;
    return false;
  },
);
